import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  HourglassEmpty as HourglassEmptyIcon,
  ExitToApp as ExitToAppIcon,
} from '@mui/icons-material';
import {
  getSquadById,
  Squad,
} from '../api/squad.api';
import { reportViewedObject } from '../hooks/usePageViewLogger';
import { squadMemberApi } from '../api/squad-member.api';
import { SquadFormDialog } from '../components/squads/SquadFormDialog';
import { SquadMemberList } from '../components/squads/SquadMemberList';
import { SquadSessions } from '../components/squads/SquadSessions';

export const SquadDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const [squad, setSquad] = useState<Squad | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [myRegistrationStatus, setMyRegistrationStatus] = useState<string | null>(null);
  const [myRegistrationNotes, setMyRegistrationNotes] = useState<string | null>(null);
  const [registering, setRegistering] = useState(false);

  const canManageSquad = user?.role === 'ADMIN' || user?.role === 'HEAD_COACH' || user?.id === squad?.coachId;
  const isPlayer = user?.role === 'PLAYER';
  const canViewMembers = user?.role === 'ADMIN' ||
                         user?.role === 'HEAD_COACH' ||
                         (user?.role === 'COACH' && user?.id === squad?.coachId);

  const fetchSquad = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError('');
      const response = await getSquadById(id);
      setSquad(response.data);
      // Record a single enriched view entry for the activity log.
      const squadName =
        (response.data && (response.data as any).name) ||
        (response.data && (response.data as any).data && (response.data as any).data.name);
      if (squadName) {
        reportViewedObject(`/squads/${id}`, squadName);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load squad');
    } finally {
      setLoading(false);
    }
  };

  const checkMyRegistration = async () => {
    if (!id || !user?.id || !isPlayer) return;
    try {
      const response = await squadMemberApi.getSquadMembers(id, false);
      const members = response.data.data?.members || [];
      const myMembership = members.find((m: any) => m.player?.id === user.id);
      if (myMembership) {
        setMyRegistrationStatus(myMembership.status);
        setMyRegistrationNotes(myMembership.notes);
      } else {
        setMyRegistrationStatus(null);
        setMyRegistrationNotes(null);
      }
    } catch (err) {
      console.error('Failed to check registration status:', err);
    }
  };

  const handleRegister = async () => {
    if (!id || !user?.id) return;
    try {
      setRegistering(true);
      setError('');
      setSuccess('');
      const response = await squadMemberApi.registerForSquad({
        squadId: id,
        playerId: user.id,
      });
      setSuccess(response.data.message || 'Registration submitted successfully!');
      checkMyRegistration();
      fetchSquad();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to register for squad');
    } finally {
      setRegistering(false);
    }
  };

  const handleWithdraw = async () => {
    if (!id || !user?.id) return;
    if (!confirm('Are you sure you want to withdraw from this squad?')) return;

    try {
      setError('');
      setSuccess('');
      await squadMemberApi.withdrawFromSquad(id, user.id);
      setSuccess('Withdrawn from squad successfully');
      setMyRegistrationStatus(null);
      checkMyRegistration();
      fetchSquad();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to withdraw from squad');
    }
  };

  useEffect(() => {
    fetchSquad();
    checkMyRegistration();
  }, [id, user?.id]);

  const getDayName = (day: number) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[day];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !squad) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error || 'Squad not found'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/squads')} sx={{ mt: 2 }}>
          Back to Squads
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/squads')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" fontWeight={700} sx={{ flexGrow: 1 }}>
          {squad.name}
        </Typography>
        {isPlayer && myRegistrationStatus === null && squad.status !== 'archived' && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<PersonAddIcon />}
            onClick={handleRegister}
            disabled={registering}
          >
            {registering ? 'Registering...' : 'Register for Squad'}
          </Button>
        )}
        {isPlayer && myRegistrationStatus && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {myRegistrationStatus === 'active' && (
              <>
                <Chip label="You're Active" color="success" size="medium" />
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={<ExitToAppIcon />}
                  onClick={handleWithdraw}
                >
                  Withdraw
                </Button>
              </>
            )}
            {myRegistrationStatus === 'pending' && (
              <Chip
                label="Pending Approval"
                color="warning"
                size="medium"
                icon={<HourglassEmptyIcon />}
              />
            )}
            {myRegistrationStatus === 'waitlist' && (
              <Chip label="On Waitlist" color="info" size="medium" />
            )}
            {myRegistrationStatus === 'withdrawn' && (
              <Chip label="Withdrawn" color="default" size="medium" />
            )}
            {myRegistrationStatus === 'rejected' && (
              <Chip label="Application Rejected" color="error" size="medium" />
            )}
          </Box>
        )}
        {canManageSquad && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => setEditDialogOpen(true)}
          >
            Edit Squad
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {isPlayer && myRegistrationStatus === 'rejected' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Application Rejected
          </Typography>
          {myRegistrationNotes && (
            <Typography variant="body2">
              Reason: {myRegistrationNotes}
            </Typography>
          )}
          {!myRegistrationNotes && (
            <Typography variant="body2">
              Your application to join this squad was rejected.
            </Typography>
          )}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Squad Details
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                {squad.skillLevel && (
                  <Chip label={squad.skillLevel.toUpperCase()} color="primary" />
                )}
                {squad.ageGroup && (
                  <Chip label={squad.ageGroup} variant="outlined" />
                )}
                <Chip
                  label={`${squad.currentCapacity} / ${squad.maxCapacity} Players`}
                  color={squad.currentCapacity >= squad.maxCapacity ? 'error' : 'success'}
                />
              </Box>

              <Typography variant="body1" color="text.secondary" paragraph>
                {squad.description || 'No description available'}
              </Typography>

              {squad.location && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Location:</strong> {squad.location}
                </Typography>
              )}

              {squad.trainingFee && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Training Fee:</strong> £{squad.trainingFee.toFixed(2)}
                </Typography>
              )}

              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Coach:</strong> {squad.coach ? `${squad.coach.firstName} ${squad.coach.lastName}` : 'Not assigned'}
              </Typography>

              {squad.schedule && squad.schedule.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Training Schedule
                  </Typography>
                  {squad.schedule.map((item, index) => (
                    <Typography key={index} variant="body2" sx={{ ml: 2 }}>
                      • {getDayName(item.dayOfWeek)}: {item.startTime} - {item.endTime}
                    </Typography>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Quick Stats
              </Typography>

              <Box sx={{ my: 2 }}>
                <Typography variant="h2" fontWeight={700}>
                  {squad.currentCapacity}
                </Typography>
                <Typography variant="body2">Active Members</Typography>
              </Box>

              <Box sx={{ my: 2 }}>
                <Typography variant="h4" fontWeight={700}>
                  {squad.maxCapacity - squad.currentCapacity}
                </Typography>
                <Typography variant="body2">Spaces Available</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {canViewMembers && (
        <Box sx={{ mb: 3 }}>
          <SquadMemberList squadId={id!} coachId={squad.coachId} />
        </Box>
      )}

      {canViewMembers && (
        <Box sx={{ mb: 3 }}>
          <SquadSessions squadId={id!} />
        </Box>
      )}

      {editDialogOpen && (
        <SquadFormDialog
          open={editDialogOpen}
          onClose={(refreshNeeded) => {
            setEditDialogOpen(false);
            if (refreshNeeded) fetchSquad();
          }}
          squad={squad}
        />
      )}
    </Container>
  );
};
